import React, {useCallback, useMemo, useState} from 'react'

interface TaskSharingDialogState {
  isOpen: boolean
  taskId?: string
  repoOwner?: string
  repoName?: string
  isShared?: boolean
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

interface TaskSharingDialogContextValue {
  dialogState: TaskSharingDialogState
  openSharingDialog: (
    taskId: string,
    repoOwner?: string,
    repoName?: string,
    isShared?: boolean,
    returnFocusRef?: React.RefObject<HTMLElement | null>,
  ) => void
  closeSharingDialog: () => void
}

const TaskSharingDialogContext = React.createContext<TaskSharingDialogContextValue | undefined>(undefined)

interface TaskSharingDialogProviderProps {
  children: React.ReactNode
}

const initialState: TaskSharingDialogState = {isOpen: false}

export function TaskSharingDialogProvider({children}: TaskSharingDialogProviderProps) {
  const [dialogState, setDialogState] = useState<TaskSharingDialogState>(initialState)

  const openSharingDialog = useCallback(
    (
      taskId: string,
      repoOwner?: string,
      repoName?: string,
      isShared?: boolean,
      returnFocusRef?: React.RefObject<HTMLElement | null>,
    ) => {
      setDialogState({isOpen: true, taskId, repoOwner, repoName, isShared, returnFocusRef})
    },
    [],
  )

  const closeSharingDialog = useCallback(() => {
    setDialogState(initialState)
  }, [])

  const value = useMemo(
    () => ({dialogState, openSharingDialog, closeSharingDialog}),
    [dialogState, openSharingDialog, closeSharingDialog],
  )

  return <TaskSharingDialogContext value={value}>{children}</TaskSharingDialogContext>
}

export function useTaskSharingDialog() {
  const context = React.use(TaskSharingDialogContext)
  if (!context) {
    throw new Error('useTaskSharingDialog must be used within a TaskSharingDialogProvider')
  }
  return context
}

/**
 * Optional version of useTaskSharingDialog that returns undefined if not within a provider.
 * Useful for components that may be rendered in contexts where sharing isn't available.
 */
export function useTaskSharingDialogOptional() {
  return React.use(TaskSharingDialogContext)
}
